import {
  Button,
  DropDownMenu,
  styled,
  Switch,
} from "@monolith-forensics/monolith-ui";
import {
  EditIcon,
  EyeIcon,
  MoreHorizontalIcon,
  PlusIcon,
  Trash2Icon,
} from "lucide-react";
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { WebHook } from "./webhooks.types";
import { DeleteWebhookModal } from "./Modals";
import { useUpdateWebhook } from "./webhooks.query";
import LoggingAPI from "@/api/logging";
import { usePermissions } from "@/hooks/usePermissions";
import { useSnackbar } from "notistack";

const ListContainer = styled.div`
  border: 1px solid ${(props) => props.theme.palette.divider};
  border-radius: 8px;

  background-color: ${(props) => props.theme.palette.background.default};
`;

const HeaderItemContent = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 10px 20px;
  min-height: 50px;
  color: ${(props) => props.theme.palette.text.secondary};
`;

const HeaderListItem: React.FC<{ total: number }> = ({ total }) => {
  const { hasPermission, MONOLITH_PERMISSIONS } = usePermissions();

  const navigate = useNavigate();
  return (
    <HeaderItemContent>
      {total === 0 ? <div>No Webhooks</div> : <div>{total} Webhooks</div>}
      <div>
        <Button
          size="xs"
          variant="subtle"
          leftSection={<PlusIcon size={16} />}
          disabled={!hasPermission(MONOLITH_PERMISSIONS.CUSTOMIZATIONS_MANAGE)}
          onClick={() => navigate("/settings/api/webhooks/new")}
        >
          New Webhook
        </Button>
      </div>
    </HeaderItemContent>
  );
};

const RowItemContent = styled.div`
  user-select: none;
  cursor: pointer;

  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 10px 20px;
  min-height: 50px;
  border-top: 1px solid ${(props) => props.theme.palette.divider};

  &:hover {
    background-color: ${(props) => props.theme.palette.action.hover};
  }
`;

const WebHookName = styled.div`
  font-weight: 600;
  // font-size: 16px;
  color: ${(props) => props.theme.palette.text.primary};
`;

const WebHookUrl = styled.div`
  font-size: 11px;
  color: ${(props) => props.theme.palette.text.secondary};
`;

const RowItem: React.FC<{ data: WebHook }> = ({ data }) => {
  const { currentUser, hasPermission, MONOLITH_PERMISSIONS } = usePermissions();
  const { enqueueSnackbar } = useSnackbar();

  const { mutate: updateWebhook } = useUpdateWebhook();

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const navigate = useNavigate();

  const handleUpdateStatus = (status: boolean) => {
    updateWebhook({
      uuid: data.uuid,
      enabled: status,
    });

    LoggingAPI.createLog({
      case_id: undefined,
      user_id: currentUser.user_id,
      message: `Updated webhook: ${data.name}.`,
      event: "webhook:update",
      object_type: "webhook",
      object_uuid: data.uuid,
    });
  };

  return (
    <RowItemContent>
      <Switch
        defaultValue={data.enabled}
        onChange={handleUpdateStatus}
        disabled={!hasPermission(MONOLITH_PERMISSIONS.CUSTOMIZATIONS_MANAGE)}
      />
      <Link
        to={`/settings/api/webhooks/${data.uuid}`}
        style={{ marginLeft: "20px", flexGrow: 1 }}
      >
        <div>
          <WebHookName>{data.name}</WebHookName>
          <WebHookUrl>{data.url}</WebHookUrl>
        </div>
      </Link>
      <DropDownMenu
        variant="subtle"
        size="xs"
        buttonProps={{
          style: { padding: "0px 4px" },
        }}
        data={[
          {
            label: "View Webhook",
            value: "view",
            leftSection: <EyeIcon size={14} />,
            onClick: () => {
              navigate(`/settings/api/webhooks/${data.uuid}`);
            },
          },
          {
            label: "Edit Webhook",
            value: "edit",
            leftSection: <EditIcon size={14} />,
            disabled: !hasPermission(
              MONOLITH_PERMISSIONS.CUSTOMIZATIONS_MANAGE
            ),
            onClick: () => {
              navigate(`/settings/api/webhooks/${data.uuid}/edit`);
            },
          },
          {
            label: "Delete Webhook",
            value: "delete",
            leftSection: <Trash2Icon size={14} color="orangered" />,
            disabled: !hasPermission(
              MONOLITH_PERMISSIONS.CUSTOMIZATIONS_MANAGE
            ),
            onClick: () => {
              setShowDeleteModal(true);
            },
          },
        ]}
        onItemSelect={(item) => {}}
      >
        <MoreHorizontalIcon size={16} />
      </DropDownMenu>
      <DeleteWebhookModal
        show={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
        }}
        onSubmit={() => {
          setShowDeleteModal(false);
          enqueueSnackbar("Webhook deleted successfully", {
            variant: "success",
          });
        }}
        onCancel={() => {
          setShowDeleteModal(false);
        }}
        defaultFormData={data}
      />
    </RowItemContent>
  );
};

export const WebhookList: React.FC<{
  data: WebHook[];
}> = ({ data }) => {
  return (
    <ListContainer>
      <HeaderListItem total={data.length} />
      <div>
        {data.map((item) => (
          <RowItem key={item.uuid} data={item} />
        ))}
      </div>
    </ListContainer>
  );
};
